import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { environment } from './environments/environment';
import { provideAnimations } from '@angular/platform-browser/animations';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { provideHttpCache } from '@ngneat/cashew';
import { LoadingInterceptor } from './app/utils/loading.interceptor';
import { APP_INITIALIZER, ErrorHandler, importProvidersFrom } from '@angular/core';
import { MessageService } from './app/utils/message.service';
import { CookieService } from 'ngx-cookie-service';
import { ErrorInterceptorService } from './app/api/services/interceptors/error-interceptor.service';
import { TokenInterceptorService } from './app/api/services/interceptors/token-interceptor.service';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import * as Sentry from '@sentry/angular';
import { provideRouter, Router, withPreloading, PreloadAllModules, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import AppRoutes from './app/app.routes';
import { MAT_DIALOG_SCROLL_STRATEGY } from '@angular/material/dialog';
import { NoopScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { MAT_RIPPLE_GLOBAL_OPTIONS, MAT_DATE_FORMATS } from '@angular/material/core';
import { AuthService } from './app/shared/services/auth.service';

// Loading Screen Functions
function showInitializationMessage() {
  // ✅ SICHER: Container erstellen
  const loadingDiv = document.createElement('div');
  loadingDiv.id = 'app-loading';
  loadingDiv.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    font-family: Arial, sans-serif;
    font-size: 16px;
    color: #333;
  `;

  // ✅ SICHER: Content mit createElement aufbauen
  const contentWrapper = document.createElement('div');
  contentWrapper.style.textAlign = 'center';

  // Spinner Container
  const spinnerContainer = document.createElement('div');
  spinnerContainer.style.marginBottom = '20px';

  // Spinner
  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #CC0F3C;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  `;

  spinnerContainer.appendChild(spinner);

  // Text
  const textDiv = document.createElement('div');
  textDiv.textContent = 'Bitte warten... Seite wird initialisiert.';

  // Zusammenbauen
  contentWrapper.appendChild(spinnerContainer);
  contentWrapper.appendChild(textDiv);
  loadingDiv.appendChild(contentWrapper);

  // ✅ SICHER: Styles als <style> Element hinzufügen
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  loadingDiv.appendChild(style);

  document.body.appendChild(loadingDiv);
}

function hideInitializationMessage() {
  const loadingDiv = document.getElementById('app-loading');
  if (loadingDiv) {
    loadingDiv.style.opacity = '0';
    loadingDiv.style.transition = 'opacity 0.3s ease-out';
    setTimeout(() => {
      if (loadingDiv.parentNode) {
        loadingDiv.parentNode.removeChild(loadingDiv);
      }
    }, 300);
  }
}

function showErrorMessage(error: any) {
  // Hide loading screen first
  hideInitializationMessage();

  // ✅ SICHER: Container erstellen
  const errorDiv = document.createElement('div');
  errorDiv.id = 'app-error';
  errorDiv.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #f5f5f5;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    font-family: Arial, sans-serif;
  `;

  const errorMessage = error?.error === 'Timeout when waiting for 3rd party check iframe message.'
    ? 'Der Authentifizierungsdienst ist nicht erreichbar.'
    : 'Die Anwendung konnte nicht initialisiert werden.';

  // ✅ SICHER: Content Box
  const contentBox = document.createElement('div');
  contentBox.style.cssText = `
    background: white;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    max-width: 500px;
    text-align: center;
  `;

  // ✅ SICHER: Icon Container
  const iconContainer = document.createElement('div');
  iconContainer.style.cssText = `
    width: 60px;
    height: 60px;
    background: #ffebee;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
  `;

  // ✅ SICHER: SVG Icon mit createElementNS
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '30');
  svg.setAttribute('height', '30');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', '#CC0F3C');
  svg.setAttribute('stroke-width', '2');

  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  circle.setAttribute('cx', '12');
  circle.setAttribute('cy', '12');
  circle.setAttribute('r', '10');

  const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  line1.setAttribute('x1', '12');
  line1.setAttribute('y1', '8');
  line1.setAttribute('x2', '12');
  line1.setAttribute('y2', '12');

  const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  line2.setAttribute('x1', '12');
  line2.setAttribute('y1', '16');
  line2.setAttribute('x2', '12.01');
  line2.setAttribute('y2', '16');

  svg.appendChild(circle);
  svg.appendChild(line1);
  svg.appendChild(line2);
  iconContainer.appendChild(svg);

  // ✅ SICHER: Title
  const title = document.createElement('h2');
  title.textContent = 'Verbindungsfehler';
  title.style.cssText = `
    margin: 0 0 15px 0;
    color: #CC0F3C;
    font-size: 24px;
    font-weight: 600;
  `;

  // ✅ SICHER: Error Message mit textContent
  const messageParagraph = document.createElement('p');
  messageParagraph.textContent = errorMessage;
  messageParagraph.style.cssText = `
    margin: 0 0 10px 0;
    color: #666;
    font-size: 16px;
    line-height: 1.5;
  `;

  // ✅ SICHER: Help Text
  const helpText = document.createElement('p');
  helpText.textContent = 'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.';
  helpText.style.cssText = `
    margin: 0 0 25px 0;
    color: #999;
    font-size: 14px;
  `;

  // ✅ SICHER: Button mit addEventListener statt onclick
  const button = document.createElement('button');
  button.textContent = 'Erneut versuchen';
  button.style.cssText = `
    background: #CC0F3C;
    height: 40px;
    line-height: 40px;
    color: white;
    border: none;
    font-size: 14px;
    min-width: 200px;
    border-radius: 24px;
    cursor: pointer;
    font-weight: bold;
    transition: background 0.2s;
  `;

  // ✅ SICHER: Event Listeners statt inline handlers
  button.addEventListener('click', () => window.location.reload());
  button.addEventListener('mouseover', () => {
    button.style.background = '#a50c31';
  });
  button.addEventListener('mouseout', () => {
    button.style.background = '#CC0F3C';
  });

  // ✅ SICHER: Details Element
  const details = document.createElement('details');
  details.style.cssText = `
    margin-top: 25px;
    text-align: left;
    cursor: pointer;
  `;

  const summary = document.createElement('summary');
  summary.textContent = 'Technische Details';
  summary.style.cssText = `
    color: #999;
    font-size: 12px;
    user-select: none;
  `;

  const pre = document.createElement('pre');
  pre.textContent = JSON.stringify(error, null, 2); // ✅ SICHER: textContent
  pre.style.cssText = `
    margin-top: 10px;
    padding: 10px;
    background: #f5f5f5;
    border-radius: 4px;
    font-size: 11px;
    color: #666;
    overflow: auto;
    max-height: 200px;
  `;

  details.appendChild(summary);
  details.appendChild(pre);

  // ✅ SICHER: Alles zusammenbauen
  contentBox.appendChild(iconContainer);
  contentBox.appendChild(title);
  contentBox.appendChild(messageParagraph);
  contentBox.appendChild(helpText);
  contentBox.appendChild(button);
  contentBox.appendChild(details);

  errorDiv.appendChild(contentBox);
  document.body.appendChild(errorDiv);
}

class CustomErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    console.error('Unhandled error:', error);

    // ✅ An Sentry weiterleiten wenn verfügbar
    if (environment.sentry.dsn) {
      Sentry.captureException(error);
    }
  }
}

// Sentry initialization for Angular 18
if (environment.sentry.dsn) {
  Sentry.init({
    environment: environment.envName,
    dsn: environment.sentry.dsn,
    release: environment.sentry.release,
    ignoreErrors: [
      // Keycloak Ignore Error (logged separately)
      'Timeout when waiting for 3rd party check iframe message.',
    ],
    integrations: [
      Sentry.browserTracingIntegration({
        enableLongTask: true,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: environment.envName === 'production' ? 0.1 : 1.0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/test-stammdaten\.sommer-gossmann\.com\/api/,
      /^https:\/\/test-.*\.smart-media\.world\/api/
    ],
    replaysSessionSampleRate: environment.envName === 'production' ? 0.01 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      return event;
    },
    debug: environment.envName !== 'production',
  });
}

function initializeKeycloak(keycloak: KeycloakService) {
  let isSug = window.location.hostname.indexOf('sug') !== -1 || window.location.hostname === 'localhost';

  console.log('isSug', window.location.hostname, isSug);

  return () =>
    keycloak.init({
      config: {
        realm: isSug ? environment.SSO_REALM_SUG : environment.SSO_REALM_BASE,
        url: isSug ? environment.LOGIN_URL_SUG : environment.LOGIN_URL_BASE,
        clientId: isSug ? environment.SSO_CLIENT_ID_SUG : environment.SSO_CLIENT_ID_BASE,
      },
      initOptions: {
        onLoad: 'login-required',
        silentCheckSsoRedirectUri:
          window.location.origin + '/assets/silent-check-sso.html',
        enableLogging: true,
        checkLoginIframe: false,
      },
      enableBearerInterceptor: false,
      bearerExcludedUrls: ['/assets']
    }).then((authenticated) => {
      return authenticated;
    });
}

export function scrollFactory(overlay: Overlay): () => NoopScrollStrategy {
  return () => overlay.scrollStrategies.noop();
}

export const MY_DATE_FORMATS = {
  parse: { dateInput: 'dd.MM.yyyy' },
  display: {
    dateInput: 'dd.MM.yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

// Enhanced Keycloak Initializer with Loading Management and Error Handling
function enhancedKeycloakInitializer(keycloak: KeycloakService): () => Promise<boolean> {
  return () => {
    return initializeKeycloak(keycloak)()
      .then((authenticated) => {
        // Hide loading screen after successful Keycloak initialization
        setTimeout(() => {
          hideInitializationMessage();
        }, 500); // Short delay for smooth transition
        return authenticated;
      })
      .catch((error) => {
        // Show user-friendly error message instead of just hiding loading screen
        console.error('Keycloak initialization failed:', error);

        // Display error message to user
        showErrorMessage(error);

        // Log to Sentry if available (but not the timeout error)
        if (environment.sentry.dsn && error?.error !== 'Timeout when waiting for 3rd party check iframe message.') {
          Sentry.captureException(error);
        }

        // Don't throw the error - we've handled it by showing the error message
        // This prevents the white screen
        return false;
      });
  };
}

// MAIN FUNCTION: Bootstrap with correct order
async function initializeApp() {
  try {
    // Show loading message immediately
    showInitializationMessage();

    // Bootstrap the Angular application
    const app = await bootstrapApplication(AppComponent, {
      providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        // OPTIMIZED: Router with preloading and performance features
        provideRouter(
          AppRoutes,
          withPreloading(PreloadAllModules), // Preload all lazy modules
          withInMemoryScrolling({
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'enabled'
          }),
          withRouterConfig({
            onSameUrlNavigation: 'reload' // Performance optimization
          })
        ),

        // Animations
        provideAnimations(),

        // CustomErrorHandler
        { provide: ErrorHandler, useClass: CustomErrorHandler },

        // OPTIMIZED: Service Worker with better strategy
        provideServiceWorker('ngsw-worker.js', {
          enabled: environment.production,
          registrationStrategy: 'registerWhenStable:10000' // Reduced from 30s to 10s
        }),

        // Material configuration
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },

        // Imports
        importProvidersFrom(
          KeycloakAngularModule  // HttpCacheInterceptorModule ENTFERNT
        ),

        // Location Strategy
        {
          provide: LocationStrategy,
          useClass: PathLocationStrategy
        },

        // HTTP Client - OHNE Cashew hier!
        provideHttpClient(withInterceptorsFromDi()),

        // Cashew - NUR provideHttpCache (NEU!)
        provideHttpCache({
          ttl: 300000,
          responseSerializer: JSON.stringify
        }),

        // Interceptors
        { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptorService, multi: true },

        // Enhanced Keycloak Initializer (with Loading and Error Management)
        {
          provide: APP_INITIALIZER,
          useFactory: enhancedKeycloakInitializer,
          multi: true,
          deps: [KeycloakService]
        },

        // Services
        AuthService,
        MessageService,
        CookieService,

        // Material configuration
        {
          provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
          useValue: { duration: 5000, verticalPosition: 'top' }
        },
        {
          provide: MAT_DIALOG_SCROLL_STRATEGY,
          useFactory: scrollFactory,
          deps: [Overlay],
        },

        // ✅ Sentry (NUR TraceService, KEIN ErrorHandler mehr!)
        ...(environment.sentry.dsn ? [
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => { },
            deps: [Sentry.TraceService],
            multi: true,
          },
        ] : []),
      ],
    });

    console.log('Angular application bootstrap completed successfully');
    return app;

  } catch (error) {
    // Show error message to user instead of just hiding loading screen
    console.error('Application initialization failed:', error);
    showErrorMessage(error);

    if (environment.sentry.dsn) {
      Sentry.captureException(error);
    }

    // Don't throw - we've shown the error to the user
    return null;
  }
}

// Application start
initializeApp().catch(error => {
  console.error('Failed to initialize application:', error);
  showErrorMessage(error);
});
